import { useEffect, useState } from "react"
import { Modal } from "../../components/modal/Modal"
import { CheckCircleIcon } from "@heroicons/react/16/solid"
import { SelectField } from "../../shared/ui/SelectField"
import { SectionsEditor, CourseSection } from "../../features/courses/ui/SectionsEditor"
import { CourseDetail, updateMentorCourseApi } from "../../features/courses/api/course.api"

type Props = {
  open: boolean
  onClose: () => void
  course: CourseDetail
  /** Called after a successful save so the parent can refetch the course detail. */
  onSaved: () => void
}

const inputCls =
  "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{children}</label>
)

const LEVEL_OPTIONS = [
  { value: "beginner", label: "Начальный" },
  { value: "intermediate", label: "Средний" },
  { value: "advanced", label: "Продвинутый" },
]

const TABS = [
  { key: "details", label: "Детали" },
  { key: "settings", label: "Настройки" },
] as const
type TabKey = (typeof TABS)[number]["key"]

/** Mentor content editing — narrower field set than the admin modal (no price, is_active, categories, mentors). */
export const EditMentorCourseModal = ({ open, onClose, course, onSaved }: Props) => {
  const [tab, setTab] = useState<TabKey>("details")
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [level, setLevel] = useState<"beginner" | "intermediate" | "advanced">("beginner")
  const [durationMinutes, setDurationMinutes] = useState(0)
  const [language, setLanguage] = useState("ru")
  const [requiresExperience, setRequiresExperience] = useState(false)
  const [sections, setSections] = useState<CourseSection[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!open) return
    setTab("details")
    setTitle(course.title)
    setDescription(course.description)
    setLevel(course.level)
    setDurationMinutes(course.duration_minutes ?? 0)
    setLanguage(course.language)
    setRequiresExperience(course.requires_experience)
    setSections(course.sections ?? [])
    setError("")
  }, [open, course])

  const handleSave = async () => {
    if (!title.trim()) return
    setSaving(true)
    setError("")
    try {
      await updateMentorCourseApi(course.id, {
        title,
        description,
        level,
        duration_minutes: durationMinutes,
        language,
        requires_experience: requiresExperience,
        sections,
      })
      onSaved()
      onClose()
    } catch {
      setError("Не удалось сохранить изменения")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Редактировать курс">
      <div className="space-y-5 pb-4">

        {/* Tabs */}
        <div className="flex items-center gap-1 -mx-6 -mt-5 mb-1 px-6 border-b border-gray-100">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "details" && (
          <div className="space-y-5">
            <div>
              <FieldLabel>Название</FieldLabel>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <FieldLabel>Описание</FieldLabel>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className={`${inputCls} resize-none`}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Уровень</FieldLabel>
                <SelectField
                  value={level}
                  onChange={(v) => setLevel(v as "beginner" | "intermediate" | "advanced")}
                  options={LEVEL_OPTIONS}
                  placeholder="Выберите уровень"
                />
              </div>
              <div>
                <FieldLabel>Язык</FieldLabel>
                <input
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  placeholder="ru"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <FieldLabel>Длительность (минут)</FieldLabel>
              <input
                value={durationMinutes === 0 ? "" : String(durationMinutes)}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) setDurationMinutes(e.target.value === "" ? 0 : Number(e.target.value))
                }}
                placeholder="например, 120"
                className={inputCls}
              />
            </div>

            <div>
              <FieldLabel>Разделы курса</FieldLabel>
              <SectionsEditor sections={sections} setSections={setSections} />
            </div>
          </div>
        )}

        {tab === "settings" && (
          <label className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl px-4 py-3">
            <span className="text-sm text-gray-700">Требуется опыт</span>
            <button
              type="button"
              onClick={() => setRequiresExperience((v) => !v)}
              className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 shadow-inner ${requiresExperience ? "bg-emerald-500" : "bg-gray-400"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all ${requiresExperience ? "left-5" : "left-0.5"}`} />
            </button>
          </label>
        )}

        {error && (
          <div className="px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 px-6 pt-4 pb-1 bg-white border-t border-gray-100 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || saving}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200"
          >
            {saving
              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <CheckCircleIcon className="w-4 h-4" />
            }
            Сохранить
          </button>
        </div>
      </div>
    </Modal>
  )
}
